import {
  Stack,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { SearchBar } from 'src/components/search-bar';
import {
  getOrganizationId,
  getUsers,
  inviteUser,
} from 'src/services/user.service';
import EmptyState from 'src/sections/empty-state';
import { UserRow } from './components/user-row';
import Iconify from 'src/components/iconify';

const COLUMNS = {
  name: {
    label: 'Name',
    width: 350,
  },
  email: {
    label: 'Email',
    width: 350,
  },
  role: {
    label: 'Role',
    width: 200,
  },
  status: {
    label: 'Status',
    width: 200,
  },
  dateAdded: {
    label: 'Date Added',
    width: 200,
  },
  actions: {
    label: '',
    width: 100,
  },
};

const ROLES = {
  ADMIN: {
    label: 'Admin',
    value: 'ADMIN',
    description: 'Can create a job and add members',
  },
  MEMBER: {
    label: 'Member',
    value: 'MEMBER',
    description: 'Cannot create a job and add members',
  },
};

const STATUS = {
  ACTIVE: {
    label: 'Active',
    value: 'ACTIVE',
  },
  INVITED: {
    label: 'Invited',
    value: 'INVITED',
  },
  INACTIVE: {
    label: 'Inactive',
    value: 'INACTIVE',
  },
};

export default function TeamsPage() {
  // hooks
  const { user: currentUser } = useAuthContext();
  const popover = usePopover();
  const { enqueueSnackbar } = useSnackbar();

  // variables
  const orgId = getOrganizationId(currentUser);

  // states
  const [users, setUsers] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState(ROLES.MEMBER.value);
  const [isInviteLoading, setIsInviteLoading] = useState(false);
  const [inviteFormErrors, setInviteFormErrors] = useState({});
  const [userSearchText, setUserSearchText] = useState('');

  // functions
  const getUsersList = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) setIsLoading(true);
        const response = await getUsers(orgId);
        if (response.success) {
          const _users = response.body?.client_users?.map((_user, index) => ({
            id: _user.user_id,
            name: _user.name,
            email: _user.email,
            role: _user.user_role,
            status: _user.client_status,
            isOwner: _user.client_owner,
            dateAdded: _user.date_added,
            inviteLink: _user.team_invite_link,
          }));

          setUsers(_users);
        } else throw new Error(response.message);
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    },
    [orgId],
  );

  function isValidEmail(email) {
    // Regular expression pattern for email validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  }

  const handleInvite = async () => {
    if (inviteEmail === '') {
      setInviteFormErrors({
        ...inviteFormErrors,
        email: 'Please enter email address',
      });
      enqueueSnackbar('Please enter email address', { variant: 'error' });
      return;
    }
    if (!isValidEmail(inviteEmail)) {
      setInviteFormErrors({
        ...inviteFormErrors,
        email: 'Please enter a valid email address',
      });
      enqueueSnackbar('Please enter a valid email address', {
        variant: 'error',
      });
      return;
    }
    if (users.find(user => user.email === inviteEmail)) {
      setInviteFormErrors({
        ...inviteFormErrors,
        email: 'User already exists',
      });
      enqueueSnackbar('User already exists', {
        variant: 'error',
      });
      return;
    }
    if (inviteRole === '') {
      setInviteFormErrors({ role: 'Please select role' });
      enqueueSnackbar('Please select role', { variant: 'error' });
      return;
    }

    try {
      setInviteFormErrors({ email: '', role: '' });
      setIsInviteLoading(true);

      const payload = {
        email: inviteEmail,
        user_role: inviteRole,
      };

      const response = await inviteUser(payload, orgId);

      if (response.success) {
        enqueueSnackbar('User invited successfully');
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsInviteLoading(false);
      setInviteEmail('');
      setInviteRole(ROLES.MEMBER.value);
      refetchUsers();
    }
  };

  const refetchUsers = useCallback(() => {
    getUsersList(false);
  }, [getUsersList]);

  const getSearchFilteredUsers = useCallback(
    _users =>
      _users.filter(user => {
        if (userSearchText === '') return true;
        return user.name
          ? user.name.toLowerCase().includes(userSearchText.toLowerCase())
          : false;
      }),
    [userSearchText],
  );

  // effects
  useEffect(() => {
    getUsersList();
  }, [getUsersList]);

  useEffect(() => {
    const _users = getSearchFilteredUsers(users);

    if (!isLoading) {
      if (_users.length === 0) setIsEmpty(true);
      else setIsEmpty(false);
    } else {
      setIsEmpty(false);
    }
  }, [getSearchFilteredUsers, isLoading, users]);

  return (
    <>
      <Stack px="60px" py="40px" minHeight="100vh">
        <Stack direction="row" alignItems="center" mb="40px">
          <Box flexGrow={1} />
          <Stack direction="row" alignItems="center" spacing="12px">
            <SearchBar
              value={userSearchText}
              onChange={e => {
                setUserSearchText(e.target.value);
              }}
            />
            <Button
              variant="contained"
              startIcon={<Iconify color="currentColor" icon="tabler:plus" />}
              onClick={event => {
                popover.onOpen(event);
              }}
            >
              Add User
            </Button>
          </Stack>
        </Stack>
        <Stack height="100%">
          <Stack
            direction="row"
            alignItems="center"
            py="26px"
            px="20px"
            borderRadius="8px"
            border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
            bgcolor="background.primaryWhite1"
            mb="24px"
            spacing="16px"
          >
            {Object.keys(COLUMNS).map(column => (
              <Typography
                key={COLUMNS[column].id}
                width={COLUMNS[column].width}
                variant="paragraph"
                fontWeight={600}
              >
                {COLUMNS[column].label}
              </Typography>
            ))}
          </Stack>
          {isEmpty && (
            <Stack
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <EmptyState />
            </Stack>
          )}
          {isLoading ? (
            <Stack width="100%" alignItems="center" justifyContent="center">
              Loading...
            </Stack>
          ) : (
            <Stack spacing="16px">
              {getSearchFilteredUsers(users).map(user => (
                <UserRow
                  key={user.id}
                  COLUMNS={COLUMNS}
                  ROLES={ROLES}
                  STATUS={STATUS}
                  user={user}
                  orgId={orgId}
                  refetchUsers={refetchUsers}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
      <CustomPopover
        open={popover.open}
        onClose={() => {
          popover.onClose();
          setInviteEmail('');
          setInviteRole(ROLES.MEMBER.value);
        }}
        sx={{
          borderColor: theme => theme.palette.border.primaryBlack3,
        }}
      >
        <Stack>
          <Typography variant="subheading2" mb="4px">
            Add User
          </Typography>
          <Typography variant="caption" color="content.primaryBlack3" mb="8px">
            Invite your team members
          </Typography>
          <Stack direction="row" alignItems="center" spacing="16px" mb="16px">
            <TextField
              id="email"
              placeholder="Enter email address"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              error={!!inviteFormErrors.email}
            />
            <Select
              id="role-select"
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value)}
              renderValue={value => ROLES[value].label}
              error={!!inviteFormErrors.role}
              sx={{
                '& .MuiOutlinedInput-input': {
                  minWidth: '100px',
                },
              }}
            >
              {Object.keys(ROLES).map(role => (
                <MenuItem
                  key={role}
                  value={role}
                  sx={{
                    px: '8px',
                    py: '4.5px',
                  }}
                >
                  <Stack spacing="4px">
                    <Typography variant="caption">
                      {ROLES[role].label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="content.primaryBlack3"
                      noWrap={false}
                    >
                      {ROLES[role].description}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <LoadingButton
            loading={isInviteLoading}
            variant="contained"
            fullWidth
            onClick={handleInvite}
          >
            Invite
          </LoadingButton>
        </Stack>
      </CustomPopover>
    </>
  );
}
